import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import type { ApiClient, IframePoolOrderItem } from '../api';
import { gameEvents } from '../postMessage';
import { useReveal } from './useReveal';
import { PackReveal } from './PackReveal';
import { RevealBackground } from './RevealBackground';
import { Button } from '../design-system/Button';
import { ChevronLeft, ChevronRight } from '../design-system';

export interface RevealScreenProps {
  client: ApiClient;
  orderId: string;
  onClose: () => void;
  /** Buyback "Accept Offer & Rip Again" path — buy another pack from
   *  the same offer. */
  onBuyAgain: () => void;
  /** "Create Shipping" path on the winner screen + the route taken
   *  when the reveal screen closes back to the pack-detail page. */
  onShowPack: () => void;
  /** "Confirm Shipping" on the winner modal → open the delivery flow for
   *  that item. */
  onShip?: (item: IframePoolOrderItem) => void;
}

/**
 * Full‑screen reveal view rendered on the Pack page when `order_id` is
 * present in the URL. Hosts the slider, top bar, ambient background and
 * delegates per‑pack flow to `<PackReveal>`.
 *
 * Multi‑pack navigation:
 *   - `activeIndex` = currently shown pack
 *   - `maxReachable` = activeIndex can advance up to the first unopened
 *     pack, preventing skipping ahead without opening in order (mirrors
 *     web `maxSlideIndex` logic).
 */
export function RevealScreen({
  client,
  orderId,
  onClose,
  onBuyAgain,
  onShowPack,
  onShip,
}: RevealScreenProps) {
  const { order, loading, error, updateItem, refetch } = useReveal(client, orderId);
  const [activeIndex, setActiveIndex] = useState(0);
  // Packs sold via buyback this session. Lives here (not in PackReveal) so
  // it survives the per-pack remount on navigation and lets us gate the
  // "return to pack page" exit on the WHOLE order being processed.
  const [soldIds, setSoldIds] = useState<Set<string>>(new Set());

  // Tell the Design Constructor (if we're embedded) that the
  // operator is now editing the reveal screen — pathname is synthetic
  // (`/reveal`); the real URL still says `/`. Listener in
  // `useIframeScene` maps it to the `reveal` scene, which gates which
  // components show up in the sidebar. On unmount we restore the
  // pathname captured at MOUNT — reading it in the cleanup would
  // pick up any unrelated navigation that happened mid-reveal.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.parent === window) return;
    const originalPathname = window.location.pathname;
    window.parent.postMessage(
      { type: 'design-constructor-route', pathname: '/reveal' },
      '*',
    );
    return () => {
      window.parent.postMessage(
        { type: 'design-constructor-route', pathname: originalPathname },
        '*',
      );
    };
  }, []);

  const items = order?.poolItems ?? [];

  // Start at the first unopened item (or the last if all opened).
  useEffect(() => {
    if (!order) return;
    const firstUnopened = order.poolItems.findIndex((i) => !i.openedAt);
    setActiveIndex(firstUnopened === -1 ? Math.max(order.poolItems.length - 1, 0) : firstUnopened);
    gameEvents.revealStarted(order.id, order.poolItems.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id]);

  const maxReachable = useMemo(() => {
    if (items.length === 0) return 0;
    for (let i = 0; i < items.length; i++) {
      const cur = items[i];
      if (cur && !cur.openedAt) return i;
    }
    return items.length - 1;
  }, [items]);

  const canPrev = activeIndex > 0;
  const canNext = activeIndex < maxReachable;

  const goPrev = () => canPrev && setActiveIndex((i) => i - 1);
  const goNext = () => canNext && setActiveIndex((i) => i + 1);

  const activeItem = items[activeIndex];

  // After a pack's buyback lands: mark it sold (and drop its offer so a
  // return visit can't double-sell), then either advance to the next pack
  // to open/sell, or — once every pack that has an offer is sold — return
  // to the pack page. A short beat lets the "Sold ✓" state register first.
  const handlePackSold = useCallback(() => {
    if (!activeItem) return;
    const soldId = activeItem.id;
    const nextSold = new Set(soldIds);
    nextSold.add(soldId);
    setSoldIds(nextSold);
    updateItem(soldId, { bestActiveOffer: null });

    // "All done" = no pack still carries a live offer that isn't sold yet.
    // Packs without an offer (or already sold) don't block the exit.
    const allSold = items.every(
      (i) => i.bestActiveOffer == null || nextSold.has(i.id),
    );

    window.setTimeout(() => {
      if (allSold) {
        gameEvents.revealComplete(orderId);
        onShowPack();
        return;
      }
      // Prefer the first still-sealed pack so the user opens it next;
      // otherwise the first opened-but-unsold pack.
      const nextUnopened = items.findIndex((i) => !i.openedAt);
      if (nextUnopened !== -1) {
        setActiveIndex(nextUnopened);
        return;
      }
      const nextUnsold = items.findIndex(
        (i) => i.bestActiveOffer != null && !nextSold.has(i.id),
      );
      if (nextUnsold !== -1) setActiveIndex(nextUnsold);
    }, 700);
  }, [activeItem, soldIds, items, updateItem, orderId, onShowPack]);

  const handleBack = () => {
    gameEvents.revealComplete(orderId);
    onClose();
  };

  if (loading) {
    return (
      <OverlayHost>
        <LoadingState />
      </OverlayHost>
    );
  }

  if (error || !order) {
    return (
      <OverlayHost>
        <ErrorState
          message={error ?? 'Failed to load order'}
          onClose={handleBack}
        />
      </OverlayHost>
    );
  }

  if (!activeItem) {
    return (
      <OverlayHost>
        <ErrorState message="This order has no items" onClose={handleBack} />
      </OverlayHost>
    );
  }

  return (
    <OverlayHost>
      {/* Slider rail. For now we show only the active pack — web does the
          same (centered single slide) even though it peeks neighbours at
          certain breakpoints. That peek can be layered on later. */}
      <div className="relative flex-1 w-full">
        <PackReveal
          key={activeItem.id}
          client={client}
          orderId={orderId}
          offer={order.poolOffer}
          item={activeItem}
          onRevealed={(updated) => updateItem(String(activeItem.id), updated)}
          refetchOrder={refetch}
          onBuyAgain={onBuyAgain}
          onShowPack={onShowPack}
          {...(onShip ? { onShip } : {})}
          onSold={handlePackSold}
        />

        {canPrev && <NavArrow direction="left" onClick={goPrev} />}
        {canNext && <NavArrow direction="right" onClick={goNext} />}
      </div>
    </OverlayHost>
  );
}

function OverlayHost({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      // `@container` enables Ready / Winner step content to branch on
      // our own width rather than the viewport — iframe can be embedded
      // at narrower dimensions than the window itself.
      className="@container fixed inset-0 z-50 flex flex-col text-white"
      role="dialog"
      aria-modal="true"
    >
      <RevealBackground />
      <div className="relative z-10 flex flex-1 flex-col">{children}</div>
    </motion.div>
  );
}

function NavArrow({
  direction,
  onClick,
}: {
  direction: 'left' | 'right';
  onClick: () => void;
}) {
  const isLeft = direction === 'left';
  // Wrapper owns the absolute positioning because Button's BASE class
  // declares `position: relative` and would otherwise win over an
  // `absolute` passed via className, flipping `right-4` into an in-flow
  // leftward offset.
  return (
    <div
      className={`absolute top-1/2 z-20 -translate-y-1/2 ${isLeft ? 'left-4' : 'right-4'}`}
    >
      <Button
        variant="glass-icon"
        size="lg"
        aria-label={isLeft ? 'Previous pack' : 'Next pack'}
        onClick={onClick}
      >
        {isLeft ? <ChevronLeft size={22} /> : <ChevronRight size={22} />}
      </Button>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-1 items-center justify-center">
      <motion.div
        className="size-14 rounded-full border-[3px] border-white/30 border-t-[color:var(--ui-accent)]"
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, duration: 0.9, ease: 'linear' }}
        aria-hidden
      />
    </div>
  );
}

function ErrorState({
  message,
  onClose,
}: {
  message: string;
  onClose: () => void;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
      <h2 className="type-headline font-bold">Something went wrong</h2>
      <p className="type-subhead opacity-80 max-w-[320px]">{message}</p>
      <Button variant="primary" size="md" onClick={onClose}>
        Back to pack
      </Button>
    </div>
  );
}
